import {
  DateRange as BarChartDateRange,
  RunningTime,
  RunningTimeDto,
  RunningTimeType,
  DateRange as AnalysisDateRange
} from '@hconnect/common/types'
import {cloneDeep} from 'lodash'
import {Moment} from 'moment'
import moment, {unitOfTime} from 'moment-timezone'

export const getZoomedDateRange = ({
  percentStart,
  percentEnd,
  totalRunningTimeMs,
  zoomedRange,
  minGranularity
}: {
  percentStart: number
  percentEnd: number
  totalRunningTimeMs: number
  zoomedRange: BarChartDateRange
  minGranularity?: unitOfTime.Diff
}) => {
  const startMoment = zoomedRange.startDate
    .clone()
    .add(percentStart * totalRunningTimeMs, 'milliseconds')
  const endMoment = zoomedRange.startDate
    .clone()
    .add(percentEnd * totalRunningTimeMs, 'milliseconds')

  return !minGranularity || endMoment.diff(startMoment, minGranularity)
    ? {startDate: startMoment, endDate: endMoment}
    : zoomedRange
}

export const getFirstStoppageId = (dateRange: BarChartDateRange, runningTimes?: RunningTimeDto[]) =>
  runningTimes
    ?.flatMap((rt) => rt.stoppages)
    ?.find(
      (stoppage) =>
        moment.utc(stoppage?.end).isAfter(dateRange.startDate) &&
        moment.utc(stoppage?.start).isBefore(dateRange.endDate) &&
        stoppage?.cockpitStoppageId
    )?.cockpitStoppageId

export function validateNewRunningTimeDate({
  current,
  next,
  newDate,
  useTechnicalDates
}: {
  current: RunningTime
  next?: RunningTime
  newDate: Moment
  useTechnicalDates?: boolean
}) {
  const beginDate = useTechnicalDates ? current.beginTechnical : current.begin
  const endDate = useTechnicalDates ? next?.endTechnical : next?.end

  const isAfterOrSameBegin = !beginDate || newDate.isSameOrAfter(beginDate)
  const isBeforeOrSameEnd = !endDate || newDate.isSameOrBefore(endDate)

  return isAfterOrSameBegin && isBeforeOrSameEnd
}

export function updateRunningTimesWithNewDate(
  index: number,
  originalRunningTimes?: RunningTime[],
  newDate?: Moment,
  useTechnicalDates?: boolean
): RunningTime[] | undefined {
  if (!originalRunningTimes) return undefined
  if (!newDate) return originalRunningTimes
  const updatedRunningTimes: RunningTime[] = cloneDeep(originalRunningTimes)

  const current = originalRunningTimes[index]
  const next = originalRunningTimes[index + 1]

  const isValidNewDate = validateNewRunningTimeDate({
    current,
    next,
    newDate,
    useTechnicalDates
  })
  const isValid = isValidNewDate && !current.stoppages?.length && !next?.stoppages?.length
  if (!isValid) return originalRunningTimes

  updatedRunningTimes[index] = {
    ...updatedRunningTimes[index],
    end: newDate,
    endTechnical: newDate
  }

  if (index + 1 < updatedRunningTimes.length) {
    updatedRunningTimes[index + 1] = {
      ...updatedRunningTimes[index + 1],
      begin: newDate,
      beginTechnical: newDate
    }
  }
  return updatedRunningTimes
}

export const getTemporaryStoppageSplitId = (index: number, id?: number) => Number(`${id}${index}`)

export function splitIntoThreeSegments(dto: RunningTimeDto): RunningTimeDto[] {
  if (!dto.beginTechnical) {
    throw new Error('Missing beginTechnical')
  }

  const start = new Date(dto.beginTechnical).getTime()
  const end = dto.endTechnical ? new Date(dto.endTechnical).getTime() : Date.now()

  if (isNaN(start) || isNaN(end) || start >= end) {
    throw new Error('Invalid beginTechnical or endTechnical dates')
  }

  const segmentDuration = (end - start) / 3

  const point1 = new Date(start + segmentDuration).toISOString()
  const point2 = new Date(start + 2 * segmentDuration).toISOString()

  return [
    {
      ...dto,
      id: getTemporaryStoppageSplitId(0, dto.id),
      begin: dto.beginTechnical,
      end: point1,
      beginTechnical: dto.beginTechnical,
      endTechnical: point1
    },
    {
      ...dto,
      id: getTemporaryStoppageSplitId(1, dto.id),
      begin: point1,
      end: point2,
      beginTechnical: point1,
      endTechnical: point2,
      runningTimeType:
        dto.runningTimeType === RunningTimeType.RUNNING
          ? RunningTimeType.STOPPED
          : RunningTimeType.RUNNING
    },
    {
      ...dto,
      id: getTemporaryStoppageSplitId(2, dto.id),
      begin: point2,
      end: dto.endTechnical,
      beginTechnical: point2,
      endTechnical: dto.endTechnical
    }
  ]
}

// TODO adjust in HCP-93976
export const prepareRunningTimesForChart = (data: RunningTime[], timeRange: AnalysisDateRange) => {
  return data
    .filter(
      (rt) =>
        rt.endTechnical?.isAfter(timeRange.startDate) &&
        rt.beginTechnical?.isBefore(timeRange.endDate)
    )
    .map((rt) => ({
      ...rt,
      begin: rt.beginTechnical?.isBefore(timeRange.startDate)
        ? timeRange.startDate
        : rt.beginTechnical,
      end: rt.endTechnical?.isAfter(timeRange.endDate) ? timeRange.endDate : rt.endTechnical
    }))
}

export const getZoomedAndFilteredData = ({
  equipmentRunningTimes,
  zoomedDateRange,
  timezone
}: {
  equipmentRunningTimes?: RunningTimeDto[]
  zoomedDateRange: BarChartDateRange
  timezone: string
}) =>
  equipmentRunningTimes
    ?.filter((rt) => rt.begin !== rt.end)
    .filter((rt) => {
      const begin = moment.tz(rt.begin, timezone)
      const end = moment.tz(rt.end, timezone)
      return end.isAfter(zoomedDateRange.startDate) && begin.isBefore(zoomedDateRange.endDate)
    })

export const getRunningTimeToUpdate = (index: number, runningTimes?: RunningTime[]) =>
  runningTimes?.[index].runningTimeType === RunningTimeType.RUNNING
    ? runningTimes?.[index + 1] // Adjust the end of the next stopped running time
    : runningTimes?.[index]
